// Semantic Analyzer entry point (ARCHITECTURE.md §11.3).
//
// `runSemanticAnalysis` wires the full opt-in pipeline:
//   gap detection → context assembly → prompt → LLM → parse → normalize
// It reads source from the file system (Layer 1) and structure from the AST
// graph (Layer 2), invokes the caller-supplied LlmAdapter, and returns candidate
// GraphEdges plus the gaps that were detected. It never runs automatically and
// never touches agent-contracts-runtime (bridged via the plugin package).

import { readFile } from "node:fs/promises";
import { join } from "node:path";

import type { AstGraph } from "../ast/types.js";
import type { SymbolInfo } from "../analyzer/types.js";
import type { GraphEdge } from "../types.js";
import { assembleSourceContext } from "./context-assembler.js";
import { detectGaps } from "./gap-detector.js";
import { normalizeCandidates } from "./normalizer.js";
import { buildPrompt } from "./prompt-builder.js";
import { parseResponse } from "./response-parser.js";
import {
  DEFAULT_MAX_CANDIDATES_PER_RUN,
  type CandidateProposal,
  type GapCandidate,
  type LlmAdapter,
} from "./types.js";

export * from "./types.js";
export * from "./gap-detector.js";
export * from "./context-assembler.js";
export * from "./snippet-extractor.js";
export * from "./prompt-builder.js";
export * from "./response-parser.js";
export * from "./normalizer.js";

export interface RunSemanticAnalysisOptions {
  astGraph: AstGraph;
  /** filePath → artifact IDs the file participates in. */
  artifactMapping: Map<string, string[]>;
  /** Root directory used to resolve file paths for source snippets. */
  projectRoot: string;
  llmAdapter: LlmAdapter;
  /** Cap on candidate edges emitted per run (default: 100). */
  maxCandidatesPerRun?: number;
  /** agent-contracts Navigation Index, when available. */
  navigationIndex?: Record<string, unknown>;
}

export interface SemanticAnalysisResult {
  edges: GraphEdge[];
  gaps: GapCandidate[];
}

/** Index the AST graph's file nodes by path → symbols. */
function symbolsByFile(astGraph: AstGraph): Map<string, SymbolInfo[]> {
  const map = new Map<string, SymbolInfo[]>();
  for (const node of astGraph.nodes.values()) {
    if (node.kind === "file") map.set(node.id, node.symbols ?? []);
  }
  return map;
}

/** Index the AST graph's edges by their originating file path. */
function edgesByFile(astGraph: AstGraph): Map<string, GraphEdge[]> {
  const map = new Map<string, GraphEdge[]>();
  for (const edge of astGraph.edges) {
    const list = map.get(edge.from) ?? [];
    list.push(edge);
    map.set(edge.from, list);
  }
  return map;
}

/** Read source for the given paths; unreadable files are skipped (best-effort). */
async function readContents(
  projectRoot: string,
  paths: Iterable<string>,
): Promise<Map<string, string>> {
  const contents = new Map<string, string>();
  await Promise.all(
    [...new Set(paths)].map(async (path) => {
      try {
        contents.set(path, await readFile(join(projectRoot, path), "utf8"));
      } catch {
        // Snippet extraction tolerates missing content.
      }
    }),
  );
  return contents;
}

/**
 * Run semantic analysis end to end. Gaps are detected deterministically, each is
 * turned into a prompt and sent to the LLM, and validated proposals are
 * normalized into candidate edges (capped at `maxCandidatesPerRun`). A failing
 * LLM call for one gap is isolated — it is skipped rather than aborting the run.
 */
export async function runSemanticAnalysis(
  options: RunSemanticAnalysisOptions,
): Promise<SemanticAnalysisResult> {
  const {
    astGraph,
    artifactMapping,
    projectRoot,
    llmAdapter,
    navigationIndex,
  } = options;
  const maxCandidates =
    options.maxCandidatesPerRun ?? DEFAULT_MAX_CANDIDATES_PER_RUN;

  const gaps = detectGaps({ astGraph, artifactMapping, navigationIndex });
  if (gaps.length === 0 || maxCandidates <= 0) {
    return { edges: [], gaps };
  }

  const symbols = symbolsByFile(astGraph);
  const edges = edgesByFile(astGraph);

  const involved: string[] = [];
  for (const gap of gaps) involved.push(gap.fileA, gap.fileB);
  const fileContents = await readContents(projectRoot, involved);

  const proposals: CandidateProposal[] = [];
  for (const gap of gaps) {
    if (proposals.length >= maxCandidates) break;
    const context = assembleSourceContext(gap, {
      fileContents,
      symbols,
      edges,
    });
    const prompt = buildPrompt(context);

    let response: string;
    try {
      response = await llmAdapter.analyze(prompt);
    } catch {
      continue; // isolate a single failed LLM call
    }
    proposals.push(...parseResponse(response));
  }

  const capped = proposals.slice(0, maxCandidates);
  const { edges: candidateEdges } = normalizeCandidates(capped);
  return { edges: candidateEdges, gaps };
}
